import React from 'react'
import { useParams, Link } from 'react-router-dom'
import dayjs from 'dayjs'
import styled from 'styled-components'
import { get } from '@ally/metronome-ui'
import { AccountDetails, Product } from '../../../types'
import { amountFormatter, maskAccountNumber } from '../../../utils/formatter'
import { useProductsContext } from '../../../services/ProductsProvider'
import { ActionPromptItem } from './ActionPromptItem'

const YEAR_IN_DAYS = 365

// TODO - replace these react-router-dom links with metronome links when query string capability added
const StyledLink = styled(Link)`
  padding: 0;
  margin: 0;
  text-decoration: none;
  font-weight: bold;
  color: ${get.colors('bluesuedeshoes')};
`

export const RenewalInstruction: React.FC<{
  account: AccountDetails
  product: Product | undefined
}> = ({ account, product }) => {
  const { id } = useParams<{ id: string }>()
  const { products } = useProductsContext()

  const {
    cdInstructionDetails,
    cdInstrTransferAccount,
    inGracePeriod,
    currentBalancePvtEncrypt,
    retirementAccountIndicator,
    acctClosedDate,
    accountStatus,
    cdMaturityDatePvtEncrypt,
    productTermDescription,
  } = account

  const {
    transferAccountId,
    nextTermInstruction,
    transferBankName,
    transferAccountNumber,
    transferAccountJointOwner,
    transferAmount,
    currentTermInterestOptions,
    defaultRenewalProductId,
    nextTermProductId,
    nextTermInterestOptions,
    nextTermProductTermDescription,
  } = cdInstructionDetails || {}

  const { accountNickname, bankName, externalAccountIndicator } =
    cdInstrTransferAccount || {}

  const willMatureWithin365Days = (): boolean => {
    const serverDate = dayjs().tz('America/New_York')
    const dateDifference = dayjs(cdMaturityDatePvtEncrypt).diff(
      serverDate,
      'days',
    )
    return dateDifference <= YEAR_IN_DAYS
  }

  const isAccountFunded = currentBalancePvtEncrypt > 0
  const isAccountActive = accountStatus.toLowerCase() === 'active'
  const isNonIraAccount = !retirementAccountIndicator
  const isAccountNotClosed = !acctClosedDate
  const isNextTermProductIdAvailable = !!nextTermProductId
  const isCdRollover = nextTermInstruction === 'Rollover'
  const isAddFundsToCd = nextTermInstruction === 'AddFundsToBalance'
  const isWithdrawalFromCd = nextTermInstruction === 'WithdrawPartialBalance'
  const isCdClosed = nextTermInstruction === 'CloseAtMaturity'
  const isNextTermInterestOptionsAvailable = !!nextTermInterestOptions

  const isNextTermOptionsAvailable =
    isCdRollover ||
    isAddFundsToCd ||
    isWithdrawalFromCd ||
    isCdClosed ||
    isNextTermInterestOptionsAvailable ||
    isNextTermProductIdAvailable

  const displayRenewalInstructions =
    willMatureWithin365Days() &&
    isAccountFunded &&
    isNonIraAccount &&
    isAccountNotClosed &&
    isAccountActive &&
    isNextTermOptionsAvailable &&
    !inGracePeriod

  if (!product || !displayRenewalInstructions) return <></>

  const isExternalAccountDeleted = transferAccountId === '-2'

  if (isExternalAccountDeleted) {
    return (
      // TODO - [FPO] - WEB-66117
      <ActionPromptItem>
        Your non-Ally Bank accounts aren&apos;t available right now. Please
        check back later. If you still can&apos;t view these accounts, give us a
        call at 1-877-247-2559.{' '}
        <StyledLink to={`/bank/manage-cds?id=${id}`}>
          Edit your renewal instructions
        </StyledLink>
      </ActionPromptItem>
    )
  }

  // istanbul ignore next // option chaining
  const disbursementAccountJointOwner =
    currentTermInterestOptions?.disbursementAccountJointOwner

  const maskedTransferAccountNumber =
    transferAccountNumber && maskAccountNumber(transferAccountNumber.toString())

  const finalBankName = bankName || transferBankName

  const transferToAnotherAccountName = (): string => {
    if (externalAccountIndicator || finalBankName) {
      return `${finalBankName} ${maskedTransferAccountNumber}`
    }
    if (disbursementAccountJointOwner) {
      return `${disbursementAccountJointOwner}'s non-Ally Bank account`
    }

    return `${accountNickname || ''} ${maskedTransferAccountNumber}`
  }

  const isTransferByCheck = transferAccountId === '-1'

  const accountName = (): string => {
    const isTransferToJointAccount = transferAccountId === '0'

    const isTransferToAnotherAccount = !(
      transferAccountJointOwner ||
      isTransferByCheck ||
      isExternalAccountDeleted
    )

    if (isTransferToJointAccount) {
      return transferAccountNumber
        ? `${accountNickname || ''} ${maskedTransferAccountNumber}`
        : `${transferAccountJointOwner}'s non-Ally Bank account`
    }

    // istanbul ignore else
    if (isTransferToAnotherAccount) {
      return transferToAnotherAccountName()
    }
    // istanbul ignore next
    return ''
  }

  const cdDescription = nextTermProductTermDescription
  const absoluteTransferAmount = transferAmount
    ? Math.abs(transferAmount)
    : undefined

  if (isAddFundsToCd && absoluteTransferAmount) {
    return (
      <ActionPromptItem id="add-funds-to-cd-message">
        At maturity, your CD will renew to a {cdDescription}. We&apos;ll add{' '}
        {amountFormatter.format(absoluteTransferAmount)} from {accountName()}.{' '}
        <StyledLink to={`/bank/manage-cds?id=${id}`}>
          Edit your renewal instructions
        </StyledLink>
      </ActionPromptItem>
    )
  }

  if (isWithdrawalFromCd && absoluteTransferAmount) {
    return isTransferByCheck ? (
      <ActionPromptItem id="is-withdrawal-from-cd-by-check">
        At maturity, your CD will renew to a {cdDescription}. We&apos;ll send
        you a check of {amountFormatter.format(absoluteTransferAmount)}.{' '}
        <StyledLink to={`/bank/manage-cds?id=${id}`}>
          Edit your renewal instructions
        </StyledLink>
      </ActionPromptItem>
    ) : (
      <ActionPromptItem id="is-withdrawal-from-cd-no-check">
        At maturity, your CD will renew to a {cdDescription}. We&apos;ll
        transfer {amountFormatter.format(absoluteTransferAmount)} to{' '}
        {accountName()}.{' '}
        <StyledLink to={`/bank/manage-cds?id=${id}`}>
          Edit your renewal instructions
        </StyledLink>
      </ActionPromptItem>
    )
  }

  if (isCdClosed) {
    return isTransferByCheck ? (
      <ActionPromptItem id="cd-closed-by-check">
        At maturity, we&apos;ll close this account for you and mail you a check.{' '}
        <StyledLink to={`/bank/manage-cds?id=${id}`}>
          Edit your renewal instructions
        </StyledLink>
      </ActionPromptItem>
    ) : (
      <ActionPromptItem id="cd-closed-no-check">
        At maturity, we&apos;ll close this account for you. We&apos;ll transfer
        the balance to {accountName()}.{' '}
        <StyledLink to={`/bank/manage-cds?id=${id}`}>
          Edit your renewal instructions
        </StyledLink>
      </ActionPromptItem>
    )
  }

  const newCdDescription = (): string => {
    if (nextTermProductTermDescription) return nextTermProductTermDescription

    const defaultRenewalProduct =
      products &&
      products.find(prod => prod.productId === defaultRenewalProductId)

    if (defaultRenewalProduct)
      return defaultRenewalProduct.terms[0].productTermDescription
    return productTermDescription || /* istanbul ignore next */ ''
  }

  // istanbul ignore else // no need to test that scenario
  if (isCdRollover) {
    return (
      <ActionPromptItem id="cd-rollover">
        At maturity, your CD will renew to a {newCdDescription()}, and
        we&apos;ll rollover your balance.{' '}
        <StyledLink to={`/bank/manage-cds?id=${id}`}>
          Edit your renewal instructions
        </StyledLink>
      </ActionPromptItem>
    )
  }
  // istanbul ignore next // option chaining
  return <></>
}
